import { Component } from '@theme/component';
import { bundleItems, formProperties, personalisationComplete } from './mug-coaster-logic.js';

let configuration;
const selections = new Map();
function loadConfig(url) {
  configuration ||= fetch(url).then(response => {
    if (!response.ok) throw new Error('configuration');
    return response.json();
  }).catch(error => { configuration = null; throw error; });
  return configuration;
}

class MugCoasterBundle extends Component {
  connectedCallback() { super.connectedCallback(); this.initialize(); }
  updatedCallback() { super.updatedCallback(); this.initialize(); }
  disconnectedCallback() { super.disconnectedCallback(); this.controller?.abort(); }

  async initialize() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.design = this.querySelector('[data-coaster-design]');
    this.variant = this.querySelector('[data-coaster-variant]');
    this.search = this.querySelector('[data-coaster-search]');
    this.button = this.querySelector('[data-coaster-add]');
    this.image = this.querySelector('img');
    this.status = this.querySelector('[role="status"]');
    this.description = this.querySelector('[data-coaster-selected]');
    this.button.disabled = true;
    this.variants = JSON.parse(this.querySelector('[data-mug-variants]').textContent);
    this.design.addEventListener('change', () => this.chooseDesign(), options);
    this.variant.addEventListener('change', () => this.showSelection(), options);
    this.search.addEventListener('input', () => this.filterDesigns(), options);
    this.button.addEventListener('click', () => this.addPair(), options);
    try {
      const config = await loadConfig(this.dataset.configUrl);
      if (this.controller.signal.aborted) return;
      const recommended = this.querySelector('[data-recommended-coaster]');
      const coaster = recommended ? JSON.parse(recommended.textContent) : null;
      this.config = { ...config, coasters: [...config.coasters] };
      if (coaster && !this.config.coasters.some(item => item.id === coaster.id)) this.config.coasters.push(coaster);
      const existing = config.mugs[this.dataset.productId];
      this.mug = existing || { expectedPrice: 1200, recommendedCoaster: coaster?.id || this.config.coasters[0]?.id };
      if (coaster) this.mug = { ...this.mug, recommendedCoaster: coaster.id, matching: true };
      if (!this.mug.recommendedCoaster || config.currency !== this.dataset.currency || !this.variants.every(item => item.price === 1200)) throw new Error('eligibility');
      this.design.replaceChildren(...this.config.coasters.map(item => new Option(item.title, item.id)));
      this.design.value = selections.get(this.dataset.productId)?.design || this.mug.recommendedCoaster;
      this.search.disabled = false;
      this.design.disabled = false;
      this.chooseDesign();
    } catch { this.status.textContent = this.dataset.error; }
  }

  filterDesigns() {
    const current = this.design.value;
    const term = this.search.value.trim().toLocaleLowerCase();
    const items = this.config.coasters.filter(item => item.title.toLocaleLowerCase().includes(term));
    this.design.replaceChildren(...items.map(item => new Option(item.title, item.id)));
    if (items.some(item => item.id === current)) this.design.value = current;
    this.chooseDesign();
  }

  chooseDesign() {
    this.coaster = this.config.coasters.find(item => item.id === this.design.value);
    this.button.disabled = !this.coaster;
    this.variant.disabled = !this.coaster;
    this.design.disabled = !this.design.options.length;
    this.variant.replaceChildren(...(this.coaster?.variants || []).map(item => new Option(item.title === 'Default Title' ? this.dataset.single : item.title, item.id)));
    const form = document.getElementById(this.dataset.formId);
    const mugVariant = this.variants.find(item => String(item.id) === form?.querySelector('[name="id"]')?.value);
    const remembered = selections.get(this.dataset.productId);
    const match = this.coaster?.variants.find(item => item.id === remembered?.variant) || this.coaster?.variants.find(item => item.title === mugVariant?.title);
    if (match) this.variant.value = match.id;
    this.showSelection();
  }

  showSelection() {
    const item = this.coaster?.variants.find(variant => variant.id === this.variant.value);
    this.image.hidden = !item;
    if (item) {
      selections.set(this.dataset.productId, { design: this.design.value, variant: this.variant.value });
      this.image.src = item.image;
      this.image.alt = `${this.coaster.title} — ${item.title}`;
      this.description.textContent = `${this.coaster.title}${item.title === 'Default Title' ? '' : ` — ${item.title}`}${this.coaster.id === this.mug.recommendedCoaster ? ` (${this.dataset.recommended})` : ''}`;
    } else this.description.textContent = '';
    this.status.textContent = item ? (this.config.reviewOnly ? this.dataset.review : '') : this.dataset.noResults;
    this.button.textContent = this.config.reviewOnly ? this.dataset.reviewButton : this.dataset.addButton;
  }

  async addPair() {
    if (this.busy) return;
    const form = document.getElementById(this.dataset.formId);
    if (!form || !form.reportValidity()) return;
    if (!personalisationComplete(form)) { this.status.textContent = this.dataset.personalisation; return; }
    const data = new FormData(form);
    const mug = this.variants.find(item => String(item.id) === String(data.get('id')));
    const coaster = this.coaster?.variants.find(item => item.id === this.variant.value);
    const quantity = Number(data.get('quantity') || 1);
    const rule = mug?.quantity_rule || { min: 1, increment: 1 };
    if (!mug?.available || quantity < rule.min || (rule.max && quantity > rule.max) || (quantity - rule.min) % rule.increment !== 0) { this.status.textContent = this.dataset.error; return; }
    let items;
    try { items = bundleItems(mug, coaster, quantity, formProperties(form)); }
    catch { this.status.textContent = this.dataset.error; return; }
    if (this.config.reviewOnly) {
      const production = Object.entries(items[0].properties).filter(([key]) => !key.startsWith('_')).map(([key, value]) => `${key}: ${value}`).join('; ');
      const design = `${this.coaster.title}${coaster.title === 'Default Title' ? '' : ` — ${coaster.title}`}`;
      this.status.textContent = `${this.dataset.checked} ${quantity} × ${this.dataset.mugLabel} + ${quantity} × ${design}. ${production}. ${this.dataset.review}`;
      return;
    }
    this.busy = true;
    this.button.disabled = true;
    try {
      const response = await fetch(new URL(`${this.dataset.rootUrl}products/${this.coaster.handle}.js`, location.origin), { cache: 'no-store' });
      if (!response.ok) throw new Error('availability');
      const current = await response.json();
      const approved = current.variants.find(item => String(item.id) === coaster.id);
      if (!approved?.available || approved.price !== 595) throw new Error('availability');
      const added = await fetch(this.dataset.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      if (!added.ok) throw new Error('basket');
      location.assign(this.dataset.cartUrl);
    } catch { this.status.textContent = this.dataset.error; this.button.disabled = false; this.busy = false; }
  }
}

if (!customElements.get('mug-coaster-bundle')) customElements.define('mug-coaster-bundle', MugCoasterBundle);
